import { gql } from "@apollo/client";
import type {
  PermitType,
  PermitRenewalPeriod,
  PermitVehicleScope,
  PermitZoneCoverageType,
  PermitTypeUsageFeeApplicability,
  PermitDocumentType,
  ZoneCoordinate,
  ISODateTime,
} from "common/types/permits";

export const GET_PERMIT_TYPES_FOR_PAYEE = gql`
  query GetPermitTypes($payeeAccountId: uuid!) {
    permitTypes: permit_type(where: { payee_account_id: { _eq: $payeeAccountId } }, order_by: { name: asc }) {
      id
      clientId: client_id
      payeeAccountId: payee_account_id
      name
      description
      pricePerRenewalPeriod: price_per_renewal_period
      renewalPeriod: renewal_period
      vehicleScope: vehicle_scope
      zoneCoverageType: zone_coverage_type
      isAutomaticVehicleAssignmentEnabled: is_automatic_vehicle_assignment_enabled
      paymentProcessingEnabledAt: payment_processing_enabled_at
      applicationSubmissionEnabledAt: application_submission_enabled_at
      usageFeeApplicability: usage_fee_applicability
      validFrom: valid_from
      validTo: valid_to
      priority
      createdAt: created_at
      updatedAt: updated_at
      requiredDocumentTypes: document_types {
        documentType: document_type {
          id
          name
          description
        }
      }
      zones(order_by: [{ zone_id: asc }]) {
        zone {
          id
          name
          location
        }
      }
    }
  }
`;

/** Raw permit type data from GraphQL response */
export interface GetPermitTypesPermitTypeRaw {
  id: string;
  clientId: string;
  payeeAccountId: string;
  name: string;
  description: string;
  pricePerRenewalPeriod: number;
  renewalPeriod: PermitRenewalPeriod;
  vehicleScope: PermitVehicleScope;
  zoneCoverageType: PermitZoneCoverageType;
  isAutomaticVehicleAssignmentEnabled: boolean;
  paymentProcessingEnabledAt: string | null;
  applicationSubmissionEnabledAt: string | null;
  usageFeeApplicability: PermitTypeUsageFeeApplicability;
  validFrom: ISODateTime | null;
  validTo: ISODateTime | null;
  priority: number;
  createdAt: ISODateTime;
  updatedAt: ISODateTime;
  requiredDocumentTypes: {
    documentType: PermitDocumentType;
  }[];
  zones: {
    zone: {
      id: string;
      name: string;
      location: {
        coordinates: [number, number][]; // [lng, lat] from PostGIS
      };
    };
  }[];
}

export interface GetPermitTypesForPayeeVars {
  payeeAccountId: string;
}

export interface GetPermitTypesForPayeeData {
  permitTypes: GetPermitTypesPermitTypeRaw[];
}

/** Transform raw GraphQL response to PermitType with ZoneCoordinates */
export function transformPermitType(raw: GetPermitTypesPermitTypeRaw): PermitType & { zones: ZoneCoordinate[] } {
  const requiredDocumentTypes = raw.requiredDocumentTypes.map((rdt) => rdt.documentType);

  const zones: ZoneCoordinate[] = raw.zones.map(({ zone }) => {
    // PostGIS returns coordinates as [lng, lat], ZoneCoordinate expects [lat, lng]
    const avgLng = zone.location.coordinates.reduce((acc, [lng]) => acc + lng, 0) / zone.location.coordinates.length;
    const avgLat = zone.location.coordinates.reduce((acc, [, lat]) => acc + lat, 0) / zone.location.coordinates.length;

    return {
      name: zone.name,
      center: [avgLat, avgLng] as [number, number],
    };
  });

  return {
    id: raw.id,
    clientId: raw.clientId,
    payeeAccountId: raw.payeeAccountId,
    name: raw.name,
    description: raw.description,
    pricePerRenewalPeriod: raw.pricePerRenewalPeriod,
    renewalPeriod: raw.renewalPeriod,
    vehicleScope: raw.vehicleScope,
    zoneCoverageType: raw.zoneCoverageType,
    isAutomaticVehicleAssignmentEnabled: raw.isAutomaticVehicleAssignmentEnabled,
    paymentProcessingEnabledAt: raw.paymentProcessingEnabledAt,
    applicationSubmissionEnabledAt: raw.applicationSubmissionEnabledAt,
    usageFeeApplicability: raw.usageFeeApplicability,
    validFrom: raw.validFrom,
    validTo: raw.validTo,
    priority: raw.priority,
    createdAt: raw.createdAt,
    updatedAt: raw.updatedAt,
    requiredDocumentTypes,
    zones,
  };
}
